import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  Search,
  GitBranch,
  Play,
  Trash2,
  MoreHorizontal,
  Eye,
  Activity,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  Pause,
  BarChart3,
  RefreshCw,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { apiClient } from '@/lib/api-client'
import { useToast } from '@/hooks/use-toast'

// ============================================
// Types
// ============================================

interface Pipeline {
  id: string
  name: string
  repository: string
  provider: string
  branch: string
  status: string
  lastRunAt: string | null
  lastDuration: number | null
  successRate: number | null
  totalRuns: number
  webhookUrl: string | null
  config: Record<string, unknown> | null
  createdAt: string
}

interface PipelineStats {
  total: number
  byStatus: Record<string, number>
  byProvider: Array<{ provider: string; count: number; avgSuccessRate: number }>
  avgSuccessRate: number
  avgDuration: number
  totalRuns: number
}

// ============================================
// Helpers
// ============================================

const providerLabels: Record<string, string> = {
  github: 'GitHub',
  gitlab: 'GitLab',
  azure_devops: 'Azure DevOps',
  bitbucket: 'Bitbucket',
}

function statusBadge(status: string) {
  switch (status) {
    case 'success':
      return <Badge className="bg-green-500/10 text-green-500 border-green-500/20"><CheckCircle2 className="mr-1 h-3 w-3" />Success</Badge>
    case 'failed':
      return <Badge className="bg-red-500/10 text-red-500 border-red-500/20"><XCircle className="mr-1 h-3 w-3" />Failed</Badge>
    case 'running':
      return <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20"><Loader2 className="mr-1 h-3 w-3 animate-spin" />Running</Badge>
    case 'pending':
      return <Badge className="bg-gray-500/10 text-gray-500 border-gray-500/20"><Pause className="mr-1 h-3 w-3" />Pending</Badge>
    default:
      return <Badge variant="secondary">{status}</Badge>
  }
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '-'
  if (seconds < 60) return `${seconds}s`
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}m ${secs}s`
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Never'
  return new Date(dateStr).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatSuccessRate(rate: number | null): string {
  if (rate === null) return '-'
  return `${rate.toFixed(1)}%`
}

function successRateColor(rate: number | null): string {
  if (rate === null) return 'text-muted-foreground'
  if (rate >= 90) return 'text-green-500'
  if (rate >= 70) return 'text-yellow-500'
  return 'text-red-500'
}

// ============================================
// Create Pipeline Dialog
// ============================================

function CreatePipelineDialog() {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [repository, setRepository] = useState('')
  const [provider, setProvider] = useState('github')
  const [branch, setBranch] = useState('main')
  const [webhookUrl, setWebhookUrl] = useState('')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post('/devops/pipelines', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devops'] })
      toast({ title: 'Pipeline created', description: 'New CI/CD pipeline has been added' })
      setOpen(false)
      resetForm()
    },
    onError: () => toast({ title: 'Error', description: 'Failed to create pipeline', variant: 'destructive' }),
  })

  function resetForm() {
    setName('')
    setRepository('')
    setProvider('github')
    setBranch('main')
    setWebhookUrl('')
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button><Plus className="mr-2 h-4 w-4" /> Add Pipeline</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add CI/CD Pipeline</DialogTitle>
          <DialogDescription>Add a new pipeline to monitor.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Pipeline Name *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Build & Deploy" />
            </div>
            <div className="space-y-2">
              <Label>Provider</Label>
              <Select value={provider} onValueChange={setProvider}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="github">GitHub</SelectItem>
                  <SelectItem value="gitlab">GitLab</SelectItem>
                  <SelectItem value="azure_devops">Azure DevOps</SelectItem>
                  <SelectItem value="bitbucket">Bitbucket</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Repository *</Label>
            <Input value={repository} onChange={(e) => setRepository(e.target.value)} placeholder="org/repo-name" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Branch</Label>
              <Input value={branch} onChange={(e) => setBranch(e.target.value)} placeholder="main" />
            </div>
            <div className="space-y-2">
              <Label>Webhook URL</Label>
              <Input value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} placeholder="https://..." />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={() => createMutation.mutate({
              name,
              repository,
              provider,
              branch,
              webhookUrl: webhookUrl || undefined,
            })}
            disabled={!name || !repository || createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating...' : 'Add Pipeline'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// Pipeline Detail Dialog
// ============================================

function PipelineDetailDialog({ pipeline, onClose }: { pipeline: Pipeline; onClose: () => void }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const triggerMutation = useMutation({
    mutationFn: () => apiClient.post(`/devops/pipelines/${pipeline.id}/trigger`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devops'] })
      toast({ title: 'Pipeline triggered', description: 'Pipeline run has been started' })
    },
    onError: () => toast({ title: 'Error', description: 'Failed to trigger pipeline', variant: 'destructive' }),
  })

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{pipeline.name}</DialogTitle>
          <DialogDescription>{pipeline.repository}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Provider</p>
              <p className="text-sm font-medium">{providerLabels[pipeline.provider] || pipeline.provider}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Status</p>
              <div className="mt-0.5">{statusBadge(pipeline.status)}</div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Branch</p>
              <p className="text-sm font-medium flex items-center gap-1">
                <GitBranch className="h-3 w-3" />{pipeline.branch}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total Runs</p>
              <p className="text-sm font-medium">{pipeline.totalRuns}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Last Run</p>
              <p className="text-sm font-medium">{formatDate(pipeline.lastRunAt)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Last Duration</p>
              <p className="text-sm font-medium">{formatDuration(pipeline.lastDuration)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Success Rate</p>
              <p className={`text-sm font-medium ${successRateColor(pipeline.successRate)}`}>
                {formatSuccessRate(pipeline.successRate)}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Webhook URL</p>
              <p className="text-sm font-medium truncate">{pipeline.webhookUrl || 'Not configured'}</p>
            </div>
          </div>

          <Button
            className="w-full"
            onClick={() => triggerMutation.mutate()}
            disabled={triggerMutation.isPending || pipeline.status === 'running'}
          >
            <Play className="mr-2 h-4 w-4" />
            {triggerMutation.isPending ? 'Triggering...' : 'Trigger Run'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// Main Page
// ============================================

export function DevopsPage() {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [providerFilter, setProviderFilter] = useState<string>('all')
  const [selectedPipeline, setSelectedPipeline] = useState<Pipeline | null>(null)
  const queryClient = useQueryClient()
  const { toast } = useToast()

  // Fetch pipelines
  const { data: pipelinesData, isLoading } = useQuery({
    queryKey: ['devops', 'pipelines', statusFilter, providerFilter],
    queryFn: () => apiClient.get<{ data: Pipeline[]; meta: unknown }>('/devops/pipelines', {
      ...(statusFilter !== 'all' && { status: statusFilter }),
      ...(providerFilter !== 'all' && { provider: providerFilter }),
      limit: 100,
    }),
  })

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['devops', 'stats'],
    queryFn: () => apiClient.get<PipelineStats>('/devops/stats'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/devops/pipelines/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devops'] })
      toast({ title: 'Deleted', description: 'Pipeline has been removed' })
    },
    onError: () => toast({ title: 'Error', description: 'Failed to delete pipeline', variant: 'destructive' }),
  })

  const triggerMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/devops/pipelines/${id}/trigger`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devops'] })
      toast({ title: 'Triggered', description: 'Pipeline run has been started' })
    },
    onError: () => toast({ title: 'Error', description: 'Failed to trigger pipeline', variant: 'destructive' }),
  })

  const pipelines = pipelinesData?.data?.data || pipelinesData?.data || []
  const stats = statsData?.data || null

  const filtered = Array.isArray(pipelines)
    ? pipelines.filter((p: Pipeline) =>
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        p.repository.toLowerCase().includes(search.toLowerCase())
      )
    : []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">DevOps / CI-CD</h1>
          <p className="text-muted-foreground">Monitor CI/CD pipelines and deployment status</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['devops'] })}
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          <CreatePipelineDialog />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Total Pipelines</p>
            </div>
            <div className="text-2xl font-bold mt-1">{stats?.total ?? 0}</div>
            <p className="text-xs text-muted-foreground mt-1">{stats?.totalRuns ?? 0} total runs</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <p className="text-xs text-muted-foreground">Avg Success Rate</p>
            </div>
            <div className={`text-2xl font-bold mt-1 ${successRateColor(stats?.avgSuccessRate ?? null)}`}>
              {stats?.avgSuccessRate !== undefined ? `${stats.avgSuccessRate.toFixed(1)}%` : '-'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Across all pipelines</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-500" />
              <p className="text-xs text-muted-foreground">Avg Duration</p>
            </div>
            <div className="text-2xl font-bold text-blue-500 mt-1">
              {formatDuration(stats?.avgDuration ?? null)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Per pipeline run</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-purple-500" />
              <p className="text-xs text-muted-foreground">By Status</p>
            </div>
            <div className="flex items-center gap-3 mt-2">
              <span className="text-xs">
                <span className="text-green-500 font-bold">{stats?.byStatus?.success ?? 0}</span> ok
              </span>
              <span className="text-xs">
                <span className="text-red-500 font-bold">{stats?.byStatus?.failed ?? 0}</span> fail
              </span>
              <span className="text-xs">
                <span className="text-blue-500 font-bold">{stats?.byStatus?.running ?? 0}</span> run
              </span>
            </div>
            {stats?.byProvider && stats.byProvider.length > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                {stats.byProvider.map(p => `${providerLabels[p.provider] || p.provider}: ${p.count}`).join(', ')}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search pipelines..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="success">Success</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="running">Running</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="unknown">Unknown</SelectItem>
          </SelectContent>
        </Select>
        <Select value={providerFilter} onValueChange={setProviderFilter}>
          <SelectTrigger className="w-[170px]"><SelectValue placeholder="Provider" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Providers</SelectItem>
            <SelectItem value="github">GitHub</SelectItem>
            <SelectItem value="gitlab">GitLab</SelectItem>
            <SelectItem value="azure_devops">Azure DevOps</SelectItem>
            <SelectItem value="bitbucket">Bitbucket</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {isLoading ? (
        <Card>
          <CardContent className="p-6">
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex gap-4">
                  <Skeleton className="h-5 w-40" />
                  <Skeleton className="h-5 w-48" />
                  <Skeleton className="h-5 w-20" />
                  <Skeleton className="h-5 w-24" />
                  <Skeleton className="h-5 w-20" />
                  <Skeleton className="h-5 w-16" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Activity className="h-12 w-12 mb-4 opacity-50 text-muted-foreground" />
            <p className="text-lg font-medium text-muted-foreground">No pipelines found</p>
            <p className="text-sm text-muted-foreground">Add your first CI/CD pipeline to get started</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Pipeline</TableHead>
                <TableHead>Repository</TableHead>
                <TableHead>Branch</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Run</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead className="text-right">Success Rate</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((pipeline: Pipeline) => (
                <TableRow key={pipeline.id} className="cursor-pointer" onClick={() => setSelectedPipeline(pipeline)}>
                  <TableCell className="font-medium">{pipeline.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                    {pipeline.repository}
                  </TableCell>
                  <TableCell>
                    <span className="text-sm flex items-center gap-1">
                      <GitBranch className="h-3 w-3 text-muted-foreground" />
                      {pipeline.branch}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{providerLabels[pipeline.provider] || pipeline.provider}</span>
                  </TableCell>
                  <TableCell>{statusBadge(pipeline.status)}</TableCell>
                  <TableCell>
                    <span className="text-sm">{formatDate(pipeline.lastRunAt)}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm font-mono">{formatDuration(pipeline.lastDuration)}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className={`text-sm font-mono ${successRateColor(pipeline.successRate)}`}>
                      {formatSuccessRate(pipeline.successRate)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setSelectedPipeline(pipeline); }}>
                          <Eye className="mr-2 h-4 w-4" /> View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => { e.stopPropagation(); triggerMutation.mutate(pipeline.id); }}
                          disabled={pipeline.status === 'running'}
                        >
                          <Play className="mr-2 h-4 w-4" /> Trigger Run
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); deleteMutation.mutate(pipeline.id); }}>
                          <Trash2 className="mr-2 h-4 w-4" /> Delete Pipeline
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Pipeline Detail Dialog */}
      {selectedPipeline && (
        <PipelineDetailDialog
          pipeline={selectedPipeline}
          onClose={() => setSelectedPipeline(null)}
        />
      )}
    </div>
  )
}
